import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import { apiService } from '../services/api';
import { rowMatchesFdaSearch } from '../utils/fdaSearch';

interface FDAApprovalRow {
  message_id?: number;
  subject?: string;
  regimen?: string;
  type?: string;
  manufacturer?: string;
  subindication?: string;
  indication_details?: string;
  approval_based_on?: string;
  endpoints?: string;
  other_endpoints?: string;
  clin_trial_name?: string;
  nct?: string;
  url_link?: string;
  created_time?: string;
}

const WIDE_BREAKPOINT = 1280;

const FDAApprovalsView: React.FC = () => {
  const [rows, setRows] = useState<FDAApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isWide, setIsWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= WIDE_BREAKPOINT);
  const location = useLocation();

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= WIDE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setSearchQuery(q);
    }
  }, [location.search]);

  useEffect(() => {
    const loadApprovals = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchFDAApprovals(5000);
        if (response?.success) {
          const nextRows = response.data || [];
          nextRows.sort((a: FDAApprovalRow, b: FDAApprovalRow) => {
            const aTime = a.created_time ? new Date(a.created_time).getTime() : 0;
            const bTime = b.created_time ? new Date(b.created_time).getTime() : 0;
            return bTime - aTime;
          });
          setRows(nextRows);
        } else {
          setError(response?.error || 'Failed to load FDA approvals');
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load FDA approvals');
      } finally {
        setLoading(false);
      }
    };
    loadApprovals();
  }, []);

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return rows;
    }
    return rows.filter((row) =>
      rowMatchesFdaSearch(searchQuery, [
        row.subject,
        row.regimen,
        row.type,
        row.manufacturer,
        row.subindication,
        row.indication_details,
        row.clin_trial_name,
        row.nct,
        row.approval_based_on,
        row.endpoints,
        row.other_endpoints,
      ]),
    );
  }, [rows, searchQuery]);

  return (
    <div className="h-screen flex flex-col text-slate-900 bg-slate-50 font-sans overflow-hidden">
      <Header />

      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-4 flex flex-col flex-1 min-h-0">
        <div className="flex-shrink-0 bg-white rounded-xl shadow-card border border-gray-100 px-4 py-3 mb-3">
          <h1 className="text-xl font-bold text-slate-900">FDA Approvals</h1>
          <p className="text-slate-500 text-xs mt-0.5">Latest FDA approval updates across oncology.</p>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-card border border-gray-100">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-card border border-gray-100 p-8">
            <p className="text-center text-rose-600 font-medium">{error}</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden">
            <div className="flex-shrink-0 px-4 py-2.5 border-b border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-2">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-slate-900">Approvals Table</h2>
                  <span className="text-xs text-slate-500">{filteredRows.length} approvals</span>
                </div>
                <div className="flex justify-center">
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search approvals..."
                    className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div className="hidden sm:block" />
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full table-fixed text-[11px] border-collapse">
                <colgroup>
                  <col style={{ width: '90px' }} />
                  <col style={{ width: isWide ? '14%' : '22%' }} />
                  <col style={{ width: '70px' }} />
                  <col style={{ width: isWide ? '12%' : '18%' }} />
                  <col style={{ width: isWide ? '14%' : '22%' }} />
                  {isWide && (
                    <>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '50px' }} />
                      <col style={{ width: '48px' }} />
                    </>
                  )}
                  {!isWide && <col style={{ width: '48px' }} />}
                </colgroup>
                <thead className="bg-slate-50 border-b border-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Date</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Regimen</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Mfr</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Tumor Type</th>
                    {isWide && (
                      <>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Endpoints</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Trial</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">NCT</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">Source</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">More</th>
                      </>
                    )}
                    {!isWide && (
                      <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-600 uppercase">More</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row, idx) => {
                    const rowKey = String(row.message_id ?? idx);
                    const expanded = expandedRows[rowKey];
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-slate-50">
                          <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap align-top">{formatDate(row.created_time)}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-900 whitespace-normal break-words align-top" title={row.regimen || ''}>{row.regimen || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words align-top" title={row.type || ''}>{row.type || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words align-top" title={row.manufacturer || ''}>{row.manufacturer || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words align-top" title={row.subindication || ''}>{row.subindication || '—'}</td>
                          {isWide && (
                            <>
                              <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words align-top" title={row.endpoints || ''}>{row.endpoints || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words align-top" title={row.clin_trial_name || ''}>{row.clin_trial_name || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-600 font-mono whitespace-normal break-words align-top" title={row.nct || ''}>{row.nct || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-600">
                                {row.url_link ? (
                                  <a href={row.url_link} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium">Open</a>
                                ) : '—'}
                              </td>
                              <td className="px-2 py-1.5">
                                <button type="button" onClick={() => setExpandedRows((p) => ({ ...p, [rowKey]: !p[rowKey] }))} className="text-indigo-600 hover:text-indigo-700 font-medium">
                                  {expanded ? 'Hide' : 'Show'}
                                </button>
                              </td>
                            </>
                          )}
                          {!isWide && (
                            <td className="px-2 py-1.5">
                              <button type="button" onClick={() => setExpandedRows((p) => ({ ...p, [rowKey]: !p[rowKey] }))} className="text-indigo-600 hover:text-indigo-700 font-medium">
                                {expanded ? 'Hide' : 'Show'}
                              </button>
                            </td>
                          )}
                        </tr>
                        {expanded && (
                          <tr className="bg-slate-50/80">
                            <td colSpan={isWide ? 10 : 6} className="px-3 py-2 text-[11px] text-slate-600 align-top">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {!isWide && (
                                  <>
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Endpoints</div>
                                      <div className="mt-0.5 whitespace-pre-wrap break-words">{row.endpoints || '—'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Trial</div>
                                      <div className="mt-0.5 whitespace-pre-wrap break-words">{row.clin_trial_name || '—'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">NCT</div>
                                      <div className="mt-0.5 font-mono">{row.nct || '—'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Source</div>
                                      <div className="mt-0.5">
                                        {row.url_link ? <a href={row.url_link} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-700">Open</a> : '—'}
                                      </div>
                                    </div>
                                  </>
                                )}
                                <div>
                                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Indication Details</div>
                                  <div className="mt-0.5 whitespace-pre-wrap break-words">{row.indication_details || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Approval Based On</div>
                                  <div className="mt-0.5 whitespace-pre-wrap break-words">{row.approval_based_on || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Other Endpoints</div>
                                  <div className="mt-0.5 whitespace-pre-wrap break-words">{row.other_endpoints || '—'}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FDAApprovalsView;
